import React from 'react'
import { Container } from 'reactstrap'
import { useApps, type Env } from '../contexts/AppsContext'
import { useDashboardData, type Ec2Status, type GithubRunner } from '../utils/useDashboardData'
import PageToolbar from './PageToolbar'
import StatusCard, { StatusRow, checkedAtLabel } from './StatusCard'
import './ReportComponents.css'
import './DashboardComponent.css'

const ec2Label = (ec2?: Ec2Status): string => `EC2 Instance${ec2 ? ` (${ec2.name ?? ec2.instanceId})` : ''}`

const ec2Detail = (ec2?: Ec2Status): string => {
  if (!ec2 || ec2.status === 'unknown') return ec2?.error ? `Unknown - ${ec2.error}` : 'Not checked yet'
  if (ec2.status === 'up') return `Running, status checks passed`
  if (ec2.state && ec2.state !== 'running') return `Instance ${ec2.state}`
  return 'Running, status checks failed'
}

const runnerLabel = (runner: GithubRunner): string => `GitHub Runner (${runner.name})`

const elapsedSince = (startedAt: string): string => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(startedAt).getTime()) / 60000))
  return minutes < 1 ? '<1m' : `${minutes}m`
}

const runnerDetail = (runner: GithubRunner): string => {
  if (runner.status !== 'up') return 'Offline'
  if (runner.busy) {
    if (runner.activeRun)
      return `Running: ${runner.activeRun.workflowName} (${elapsedSince(runner.activeRun.startedAt)})`
    return 'Online, busy'
  }
  return 'Online, idle'
}

const DashboardComponent: React.FC = () => {
  const { apps } = useApps()
  const { summaries, health, ciStatus, infraStatus, loading, isSyncing, lastSync, error, refresh } = useDashboardData(
    'core',
    true,
  )

  if (loading) {
    return (
      <Container className="dashboard-container">
        <p>Loading dashboard...</p>
      </Container>
    )
  }

  return (
    <Container fluid className="dashboard-container">
      <PageToolbar
        error={error}
        lastSync={lastSync}
        onSync={refresh}
        syncing={isSyncing}
        disabled={loading}
        syncLabel="Refresh"
      >
        {infraStatus && (
          <div className="dashboard-toolbar__infra">
            <StatusRow
              label={ec2Label(infraStatus.ec2)}
              status={infraStatus.ec2.status}
              detail={ec2Detail(infraStatus.ec2)}
              title={checkedAtLabel(infraStatus.ec2.checkedAt) ?? undefined}
            />
            {infraStatus.githubRunners.runners.length === 0 ? (
              <StatusRow
                label="GitHub Runner"
                status="unknown"
                detail={infraStatus.githubRunners.error ?? 'No self-hosted runners found'}
              />
            ) : (
              infraStatus.githubRunners.runners.map(runner => (
                <StatusRow
                  key={runner.name}
                  label={runnerLabel(runner)}
                  status={runner.status}
                  detail={runnerDetail(runner)}
                  link={runner.activeRun?.runUrl}
                />
              ))
            )}
          </div>
        )}
      </PageToolbar>

      <div className="dashboard-rows">
        {summaries.map(product => {
          const app = apps.find(a => a.key === product.key)
          const productHealth = health.find(h => h.key === product.key)
          const envs: Env[] = app ? app.envs.map(e => e.env) : ['dev', 'prod']

          return (
            <div className="product-row" key={product.key}>
              {envs.map(env => {
                const envInfo = app?.envs.find(e => e.env === env)
                return (
                  <StatusCard
                    key={`${product.key}-${env}`}
                    label={product.label}
                    to={`/reports/${product.key}`}
                    env={env}
                    envSummary={product[env]}
                    fe={productHealth?.fe[env]}
                    be={productHealth?.be[env]}
                    showFe={envInfo?.hasFe ?? true}
                    showBe={envInfo?.hasBe ?? true}
                    ci={ciStatus.find(c => c.key === product.key)}
                  />
                )
              })}
            </div>
          )
        })}
      </div>
    </Container>
  )
}

export default DashboardComponent
